import _ from 'lodash';
import React, {
  ReactNode,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  MutableRefObject,
} from 'react';
import './style.scss';

type ResponsiveColumn = {
  name: string;
  key?: string;
  element: ReactNode;
};

type Props = {
  columns: (ResponsiveColumn | undefined)[];
  navigateTo?: NavigationRef;
  autoHideTab?: boolean;
  reverse?: boolean;
  className?: string;
  defaultTab?: number;
};

export type NavigateFunction = (tab: number) => void;
export type NavigationRef = MutableRefObject<NavigateFunction | undefined>;

const ResponsiveColumns = ({
  columns,
  navigateTo,
  autoHideTab = true,
  reverse = false,
  className = '',
  defaultTab = 0,
}: Props) => {
  const filteredColumns = useMemo(() => _.compact(columns), [columns]);
  const [selectedTab, setSelectedTab] = useState(0);
  const columnsContainer = useRef<HTMLDivElement>(null);
  const tracker = useRef<HTMLDivElement>(null);

  const showTabBar = filteredColumns.length > 1 || !autoHideTab;

  useEffect(() => {
    if (defaultTab > 0) {
      onSelectTab(filteredColumns[defaultTab]?.name!);
    }
  }, [defaultTab]);

  const onSelectTab = useCallback(
    (name: string) => {
      const idx = filteredColumns.map((it) => it!.name).indexOf(name);
      setSelectedTab(idx);
      if (!columnsContainer.current) {
        return;
      }
      setTimeout(() => {
        const container = columnsContainer.current;
        const width = container?.getBoundingClientRect().width;
        if (!width || !container) {
          return;
        }
        container.scrollLeft = width * idx;
      });
    },
    [filteredColumns],
  );

  if (navigateTo) {
    navigateTo.current = (idx: number) => {
      onSelectTab(filteredColumns[idx]?.name!);
    };
  }

  const onScroll = useCallback(
    function (this: HTMLDivElement) {
      const numColumns = filteredColumns.length;
      const fullWidth = columnsContainer.current?.getBoundingClientRect().width!;
      const fraction = this.scrollLeft / (fullWidth * numColumns);
      const trackerElem = tracker.current;

      if (trackerElem) {
        const trackerOffset = fraction * fullWidth;
        trackerElem.style.left = `${trackerOffset}px`;
      }

      const page = Math.round(fraction * filteredColumns.length);
      setSelectedTab(page);
    },
    [filteredColumns],
  );

  useEffect(() => {
    const container = columnsContainer.current;
    container?.addEventListener('scroll', onScroll);

    return () => {
      container?.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useEffect(() => {
    const fullWidth = columnsContainer.current?.getBoundingClientRect().width;
    if (tracker.current) {
      tracker.current!.style.width = `${fullWidth! / filteredColumns.length}px`;
    }
  }, [filteredColumns]);

  let finalClassName = 'reactive-columns';
  if (reverse) finalClassName += ' reverse';
  if (className) finalClassName += ` ${className}`;
  return (
    <div className={finalClassName} data-num-cols={filteredColumns.length}>
      {showTabBar && (
        <div className="reactive-columns-tab-bar">
          {filteredColumns.map((it, idx) => (
            <ReactiveColumnTab
              selected={selectedTab === idx}
              name={it!.name}
              key={it!.key ?? it!.name}
              onClick={onSelectTab}
            />
          ))}
          <div className="tracker" ref={tracker} />
        </div>
      )}
      <div className="reactive-columns-container" ref={columnsContainer}>
        {filteredColumns.map((column, idx) => {
          const key = column.name || idx;
          return <React.Fragment key={key}>{column.element}</React.Fragment>;
        })}
      </div>
    </div>
  );
};

function ReactiveColumnTab({
  name,
  onClick,
  selected,
}: {
  name: string;
  onClick: (name: string) => void;
  selected?: boolean;
}) {
  return (
    <div className="reactive-column-tab" onClick={() => onClick(name)} data-selected={selected}>
      {name}
    </div>
  );
}

export default ResponsiveColumns;
